import React, { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import BreadcrumbComp from "../layouts/BreadcrumbComp";
import { AuthContext } from "../../contexts/AuthContext";
import { useDispatch } from "react-redux";
import { menuActions } from "../../store/menu-slice";
import { exportToExcel } from "../utils/ExcelCreation";
import errorImage from "../../icons/error-tick-logo.svg";
import { getAllDisposalIntimationForms, getReportsDataWithWorkflow } from "../../services/disposal-intimation-service";
import { getAllLocationForDivision } from "../../services/location-master-service";
import { getCurrentDate, getFinancialYearStart, formatDateInDDMMYYYY, RenderIf } from "../../functions/common-functions";
import { BiSelectMultiple } from "react-icons/bi";
import SingleValuePopUp from "../utils/SingleValuePopUp";
import { Multiselect } from 'multiselect-react-dropdown';
import './MultiSelectStyles.css';
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import SearchSelect from "../common/SearchSelect";
import { getAllUsers, getDivisions, getValidUserData } from '../../services/user-service';

const DisposalIntimationReports = () => {
  const MySwal = withReactContent(Swal);
  const context = useContext(AuthContext);
  const dispatch = useDispatch();
  dispatch(menuActions.setMenu("Disposal Intimation Reports"));
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    intimationNumber: '',
    dateFrom: getFinancialYearStart(),
    dateTo: getCurrentDate(),
    initiatorUserId: '',
    initiatorName: '',
    division: context.role === 'FINAPPROVER' || context.role === 'ADMIN' ? '' : context.division,
    location: '',
    assetNonAssetItem: '',
    itemCode: '',
    status: []
  });

  // Dropdown data
  const [filterData, setFilterData] = useState({
    users: [],
    locations: [],
    items: [],
    statuses: [
      'Pending for Divisional Approval',
      'Approved by Division',
      'Pending for Corporate Finance Approval',
      'Rework',
      'Approved',
      'Deleted'
    ],
    divisions: [],
    assetTypes: [
      'Asset Item',
      'Non-Asset Item',
      'Stock Items',
      'Insurance Claim Items for salvage value',
      'All'
    ]
  });

  // Popup states
  const [showForm, setShowForm] = useState(false);
  const [popUp, setPopUp] = useState({
    name: '',
    type: '',
    key: '',
    value: ''
  });

  // Validation errors
  const [errors, setErrors] = useState({});

  // Autocomplete states
  const [intimationSuggestions, setIntimationSuggestions] = useState([]);
  const [userIdSuggestions, setUserIdSuggestions] = useState([]);
  const [showIntimationSuggestions, setShowIntimationSuggestions] = useState(false);
  const [showUserIdSuggestions, setShowUserIdSuggestions] = useState(false);


  useEffect(() => {
    fetchFilterData();
  }, []);

  const fetchFilterData = async () => {
    try {
      const data = await getAllDisposalIntimationForms();
      const allDivisions = await getDivisions();

      const allUsers = await getAllUsers();

      const uniqueIntimationNumbers = [...new Set(data.map(item => item.IntimationNumber))]
        .filter(Boolean);

      console.log("uniqueIntimationNumbers", uniqueIntimationNumbers);

      const uniqueItems = [...new Set(data.map(item => item.ItemCode))]
        .filter(Boolean)
        .map(itemCode => {
          const item = data.find(d => d.ItemCode === itemCode);
          return {
            ItemCode: itemCode,
            ItemDescription: item?.ItemDescription || ''
          };
        });

      const uniqueStatuses = [...new Set(data.map(item => item.Status))]
        .filter(Boolean);

      setFilterData(prev => ({
        ...prev,
        users: allUsers,
        divisions: allDivisions,
        items: uniqueItems,
        statuses: uniqueStatuses.length > 0 ? uniqueStatuses : prev.statuses,
        intimationNumbers: uniqueIntimationNumbers
      }));

      // Fetch locations for current division
      if (context.division) {
        fetchLocationsByDivision(context.division);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  };

  const fetchLocationsByDivision = async (division) => {
    try {
      let locations;
      if (context.role === 'FINAPPROVER' || context.role === 'ADMIN') {
        locations = await getAllLocationForDivision(division);
      } else {
        const data = await getValidUserData("UserMaster", "UserId", context.userId)
        const locationsData = data[0].Location.split(",").map(item => item.trim());

        locations = locationsData.map(item => ({
          LocationCode: locationsData.indexOf(item),
          Location: item
        }));
      }

      setFilterData(prev => ({
        ...prev,
        locations: locations.map(loc => ({
          LocationName: loc.Location,
          LocationCode: loc.LocationCode
        }))
      }));
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const handleInputChange = (field, value) => {
    // Input validation
    if (field === 'intimationNumber' && value.length > 30) return;
    if (field === 'initiatorUserId' && value.length > 20) return;
    if (field === 'initiatorName' && value.length > 100) return;

    // Alphanumeric validation for intimation number
    if (field === 'intimationNumber' && value && !/^[a-zA-Z0-9\/\-]*$/.test(value)) return;

    // Alphanumeric with dot validation for user ID
    if (field === 'initiatorUserId' && value && !/^[a-zA-Z0-9\.]*$/.test(value)) return;

    // Alphanumeric with space validation for name
    if (field === 'initiatorName' && value && !/^[a-zA-Z0-9 ]*$/.test(value)) return;

    setFilters(prev => ({ ...prev, [field]: value }));

    // Auto-fill related fields
    if (field === 'initiatorUserId' && value) {
      const user = filterData.users.find(u => u.UserId === value);
      if (user) {
        setFilters(prev => ({
          ...prev,
          initiatorName: user.UserName,
          division: user.Division
        }));
      }
    }

    if (field === 'division' && value) {
      fetchLocationsByDivision(value);
      setFilters(prev => ({ ...prev, location: '' })); // Reset location
    }


    // Handle autocomplete
    if (field === 'intimationNumber') {
      if (filterData.intimationNumbers && filterData.intimationNumbers.length > 0) {
        const suggestions = filterData.intimationNumbers.filter(num =>
          num.toLowerCase().includes(value.toLowerCase())
        );
        setIntimationSuggestions(suggestions.slice(0, 5));
        setShowIntimationSuggestions(value.length > 0 && suggestions.length > 0);
      } else {
        setShowIntimationSuggestions(false);
      }
    }

    if (field === 'initiatorUserId') {
      const suggestions = filterData.users?.filter(user =>
        user.UserId && user.UserId.toLowerCase().includes(value.toLowerCase())
      ) || [];
      setUserIdSuggestions(suggestions.slice(0, 5));
      setShowUserIdSuggestions(value.length > 0 && suggestions.length > 0);
    }

    // Clear errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateFilters = () => {
    const newErrors = {};

    // Date validation
    if ((filters.dateFrom && !filters.dateTo) || (!filters.dateFrom && filters.dateTo)) {
      newErrors.dateRange = "Please enter both from and to date";
    }

    if (filters.dateFrom && filters.dateTo && new Date(filters.dateFrom) > new Date(filters.dateTo)) {
      newErrors.dateRange = "To date should be greater than or equal to From date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const setPopUpData = (data) => {
    if (data.name === 'User Master') {
      handleInputChange('initiatorUserId', data.result.UserId);
      handleInputChange('initiatorName', data.result.UserName);
      handleInputChange('division', data.result.Division);
    } else if (data.name === 'Item Master') {
      handleInputChange('itemCode', data.result.ItemCode);
    } else if (data.name === 'Location Master' || data.name.startsWith('Location Master:')) {
      handleInputChange('location', data.result.Location);
    }
  };

  const showPopUp = (name, type, key, value) => {
    setPopUp({ name, type, key, value });
    setShowForm(true);
  };

  const handleExport = async () => {
    if (!validateFilters()) return;

    setLoading(true);
    try {
      const filteredData = await getReportsDataWithWorkflow({
        ...filters,
        userRole: context.role,
        userDivision: context.division
      });

      if (!filteredData || filteredData.length === 0) {
        MySwal.fire({
          text: "No Records Found.",
          imageUrl: errorImage,
          customClass: "swal-success",
        });
        return;
      }

      const excelData = filteredData.map(item => ({
        IntimationNumber: item.IntimationNumber || '',
        IntimationDate: item.IntimationDate ? formatDateInDDMMYYYY(item.IntimationDate) : '',
        InitiatorUserID: item.InitiatorUserID || '',
        InitiatorName: item.InitiatorName || '',
        InitiatorDivision: item.InitiatorDivision || '',
        InitiatorLocation: item.InitiatorLocation === "Other" ? item.IfOtherLoc : item.InitiatorLocation || '',
        LogisticCompany: item.LogisticCompany || '',
        AssetNonAssetItem: item.AssetNonAssetItem || '',
        ItemCode: item.ItemCode || '',
        ItemDescription: item.ItemDescription || '',
        ItemDetails: item.ItemDetails || '',
        ItemType: item.ItemType || '',
        ProductClass: item.ProductClass || '',
        CommodityCodes: item.CommodityCodes || '',
        Quantity: item.Quantity || '',
        UOM: item.UOM || '',
        GSTRate: item.GSTRate || '',
        Status: item.Status || '',
        PendingWith: item.PendingWith || '',
        DivisionalApproverEmailId: item.DivisionalApproverEmailId || '',
        DivisionalApproverApprovedOn: item.DivisionalApproverApprovedOn || '',
        DivisionalApproverRemarks: item.DivisionalApproverRemarks || '',
        FinalApproverEmailId: item.FinalApproverEmailId || '',
        FinalApproverApprovedOn: item.FinalApproverApprovedOn || '',
        FinalApproverRemarks: item.FinalApproverRemarks || '',
        ReasonForDeletion: item.ReasonForDeletion || ''
      }));

      const colNames = [
        "Disposal Intimation Number", "Date", "Initiator User ID", "Initiator Name",
        "Division", "Location", "If Other Location", "Logistic Company", "Asset/Non-Asset Item",
        "Item Code", "Item Description", "Item Details", "Item Type", "Product Class",
        "Commodity Code", "Quantity", "UOM", "GST Rate", "Status", "Pending With",
        "Divisional Approver Email ID", "Divisional Approver approved on", "Divisional Approver Remarks",
        "Final Approver Email ID", "Final Approver approved on", "Final Approver Remarks", "Reason for deletion"
      ];

      const fieldNames = [
        "IntimationNumber", "IntimationDate", "InitiatorUserID", "InitiatorName",
        "InitiatorDivision", "InitiatorLocation", "IfOtherLoc", "LogisticCompany", "AssetNonAssetItem",
        "ItemCode", "ItemDescription", "ItemDetails", "ItemType", "ProductClass",
        "CommodityCodes", "Quantity", "UOM", "GSTRate", "Status", "PendingWith",
        "DivisionalApproverEmailId", "DivisionalApproverApprovedOn", "DivisionalApproverRemarks",
        "FinalApproverEmailId", "FinalApproverApprovedOn", "FinalApproverRemarks", "ReasonForDeletion"
      ];

      exportToExcel("Disposal_Intimation_Reports", "Reports", excelData, "A1:AB1", colNames, fieldNames);

    } catch (error) {
      console.error("Export error:", error);
      MySwal.fire({
        text: "Failed to export data. Please try again.",
        imageUrl: errorImage,
        customClass: "swal-success",
      });
    } finally {
      setLoading(false);
    }
  };

    const resetFilters = () => {
    setFilters({
      intimationNumber: '',
      dateFrom: '',
      dateTo: '',
      initiatorUserId: '',
      initiatorName: '',
      division: '',
      location: '',
      assetNonAssetItem: '',
      itemCode: '',
      status: [],
    });
    setErrors({});
  };

  return (
    <>
      {loading && <div className="spinner-box"><Spinner animation="border" className="spinner" /></div>}
      <BreadcrumbComp />
      <Container fluid className="main-container">
        <div className="disposal-form">
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="g-input position-relative">
                  <Form.Control
                    id="intimationNumber"
                    type="text"
                    name="intimationNumber"
                    placeholder=" "
                    value={filters.intimationNumber}
                    onChange={(e) => handleInputChange('intimationNumber', e.target.value)}
                    onFocus={() => {
                      if (filters.intimationNumber && filterData.intimationNumbers) {
                        const suggestions = filterData.intimationNumbers.filter(num =>
                          num.toLowerCase().includes(filters.intimationNumber.toLowerCase())
                        );
                        setIntimationSuggestions(suggestions.slice(0, 5));
                        setShowIntimationSuggestions(suggestions.length > 0);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowIntimationSuggestions(false), 200)}
                    maxLength={30}
                  />
                  <Form.Label htmlFor="intimationNumber">Disposal Intimation Number</Form.Label>
                  {showIntimationSuggestions && (
                    <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 1000, width: '100%', top: '100%' }}>
                      {intimationSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className="p-2 cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onMouseDown={() => {
                            handleInputChange('intimationNumber', suggestion);
                            setShowIntimationSuggestions(false);
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#F9F2F6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
                        >
                          {suggestion}
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="dateFrom"
                    type="date"
                    name="dateFrom"
                    placeholder=" "
                    value={filters.dateFrom}
                    onChange={(e) => handleInputChange('dateFrom', e.target.value)}
                  />
                  <Form.Label htmlFor="dateFrom">Disposal Raised Date From</Form.Label>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="dateTo"
                    type="date"
                    name="dateTo"
                    placeholder=" "
                    value={filters.dateTo}
                    onChange={(e) => handleInputChange('dateTo', e.target.value)}
                  />
                  <Form.Label htmlFor="dateTo">Disposal Raised Date To</Form.Label>
                  {errors.dateRange && (
                    <Form.Text className="text-danger">
                      {errors.dateRange}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="g-input">
                  <div className="d-flex align-items-center position-relative">
                    <Form.Control
                      id="initiatorUserId"
                      type="text"
                      name="initiatorUserId"
                      placeholder=" "
                      value={filters.initiatorUserId}
                      onChange={(e) => handleInputChange('initiatorUserId', e.target.value)}
                      onFocus={() => setShowUserIdSuggestions(false)}
                      onBlur={() => setTimeout(() => setShowUserIdSuggestions(false), 300)}
                      maxLength={20}
                    />
                    <Form.Label htmlFor="initiatorUserId">Initiator User ID</Form.Label>
                    <BiSelectMultiple
                      size={20}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowUserIdSuggestions(false);
                        showPopUp('User Master', 'Single', 'UserId', filters.initiatorUserId);
                      }}
                      color="#707070"
                      style={{ marginLeft: "-2rem", cursor: "pointer", zIndex: 1001 }}
                    />
                    {showUserIdSuggestions && userIdSuggestions.length > 0 && (
                      <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 999, width: 'calc(100% - 2rem)', top: '100%' }}>
                        {userIdSuggestions.map((user, index) => (
                          <div
                            key={index}
                            className="p-2 cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleInputChange('initiatorUserId', user.UserId);
                              handleInputChange('initiatorName', user.UserName);
                              handleInputChange('division', user.Division);
                              setShowUserIdSuggestions(false);
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#F9F2F6'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#FFFFFF'}
                          >
                            {user.UserId} - {user.UserName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="initiatorName"
                    type="text"
                    name="initiatorName"
                    placeholder=" "
                    value={filters.initiatorName}
                    onChange={(e) => handleInputChange('initiatorName', e.target.value)}
                    maxLength={100}
                  />
                  <Form.Label htmlFor="initiatorName">Initiator Name</Form.Label>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                {context.role === 'FINAPPROVER' || context.role === 'ADMIN' ? (
                  <SearchSelect
                    name="division"
                    label="Division "
                    data={filterData.divisions}
                    value={filters.division}
                    onChange={(e) => handleInputChange('division', e.target.value)}
                    error={errors}
                    setError={setErrors}
                  />
                ) : (
                  <Form.Group className="g-input">
                    <Form.Control
                      id="division"
                      type="text"
                      name="division"
                      placeholder=" "
                      value={filters.division}
                      readOnly={filters.initiatorUserId ? false : true}
                    />
                    <Form.Label htmlFor="division">Division</Form.Label>
                  </Form.Group>
                )}
              </Col>

              <Col md={6}>
                <Form.Group className="g-input">
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      value={filters.location}
                      placeholder=" "
                      readOnly
                    />
                    <Form.Label>Location</Form.Label>
                    <BiSelectMultiple
                      size={20}
                      onClick={() => {
                        if (filters.initiatorUserId) {
                          showPopUp(`Location Master:USER:${filters.initiatorUserId}`, 'Single', 'Location', filters.location);
                        } else if (context.role === 'FINAPPROVER' || context.role === 'ADMIN') {
                          if (filters.division) {
                            showPopUp(`Location Master:DIVISION:${filters.division}`, 'Single', 'Location', filters.location);
                          } else {
                            showPopUp(`Location Master`, 'Single', 'Location', filters.location);
                          }
                        } else {
                          showPopUp(`Location Master:USER:${context.userId}`, 'Single', 'Location', filters.location);
                        }
                      }}
                      color="#707070"
                      style={{ marginLeft: "-2rem", cursor: "pointer" }}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <SearchSelect
                  name="assetNonAssetItem"
                  label="Asset/Non-Asset Items"
                  data={filterData.assetTypes}
                  value={filters.assetNonAssetItem}
                  onChange={(e) => handleInputChange('assetNonAssetItem', e.target.value)}
                  error={errors}
                  setError={setErrors}
                />
              </Col>

              <Col md={6}>
                <Form.Group className="g-input">
                  <div className="d-flex align-items-center">
                    <Form.Control
                      type="text"
                      value={filters.itemCode}
                      placeholder=" "
                      readOnly
                    />
                    <Form.Label>Item Code</Form.Label>
                    <BiSelectMultiple
                      size={20}
                      onClick={() => showPopUp('Item Master', 'Single', 'ItemCode', filters.itemCode)}
                      color="#707070"
                      style={{ marginLeft: "-2rem", cursor: "pointer" }}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div style={{ position: 'relative' }}>
                  <Multiselect
                    options={[{ key: 'Select All' }, ...filterData.statuses.map(status => ({ key: status }))]}
                    selectedValues={filters.status.map(status => ({ key: status }))}
                    onSelect={(selectedList, selectedItem) => {
                      if (selectedItem.key === 'Select All') {
                        setFilters(prev => ({ ...prev, status: filterData.statuses }));
                      } else {
                        setFilters(prev => ({ ...prev, status: selectedList.filter(item => item.key !== 'Select All').map(item => item.key) }));
                      }
                    }}
                    onRemove={(selectedList, removedItem) => {
                      if (removedItem.key === 'Select All') {
                        setFilters(prev => ({ ...prev, status: [] }));
                      } else {
                        setFilters(prev => ({ ...prev, status: selectedList.filter(item => item.key !== 'Select All').map(item => item.key) }));
                      }
                    }}
                    displayValue="key"
                    placeholder="Status"
                    showCheckbox
                    className="custom-multiselect"
                  />
                  <BiSelectMultiple
                    size={20}
                    color="#707070"
                    style={{ position: 'absolute', right: '35px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={12} className="text-center">
                <Button
                  variant="success"
                  className="btn-export-color"
                  onClick={handleExport}
                  disabled={loading}
                >
                  {loading ? 'Exporting...' : 'Export to Excel'}
                </Button>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <Button
                  variant="success"
                  className="btn-export-color me-2"
                  onClick={resetFilters}
                  disabled={loading}
                >
                  Reset Filters
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        {/* Popups */}
        <RenderIf isTrue={popUp.type === 'Single' && showForm}>
          <SingleValuePopUp
            show={showForm}
            onHide={() => setShowForm(false)}
            setSelected={setPopUpData}
            mastername={popUp.name}
            paramkey={popUp.key}
            paramvalue={popUp.value}
            backdrop="static"
          />
        </RenderIf>
      </Container>
    </>
  );
};

export default DisposalIntimationReports;